import { atom } from "recoil";
import { Day, Uid } from "../types/core";
import { Job } from "../types/job";
import { atomWithPersistence } from "../utils/persistence";

/** What the Recurring routes card shows: the list, one route by id, or a new route being set up. */
export type RecurringView = { kind: "list" } | { kind: "route"; id: string } | { kind: "new" };

export const recurringViewState = atom<RecurringView>({
    key: "recurring.view",
    default: { kind: "list" },
});

/** A new route filled in from somewhere (a person's day on the board, a job); read once by the card. */
export type RecurringDraft = {
    name: string;
    jobIds: string[];
    assigneeUid?: Uid;
    isoWeekday?: number;
    startDay?: Day;
    /** the day it came from, for the card's line "From Grace's day on Mon 19 Oct" */
    fromDay?: Day;
};

export const recurringDraftState = atom<RecurringDraft | undefined>({
    key: "recurring.draft",
    default: undefined,
});

/** A new route's stop open in the Job Sheet as a draft; Done hands the changed stop back to the route's card. */
export type RecurringStopEdit = { onDone: (job: Job) => void };

export const recurringStopEditState = atom<RecurringStopEdit | undefined>({
    key: "recurring.stopEdit",
    default: undefined,
});

/**
 * The route whose Edit stops is open, with its stops as they were when it opened. Picking a place on the map hides
 * the card for a moment; this brings it back still in Edit stops.
 */
export const recurringStopsOpenState = atom<{ id: string; before?: string } | undefined>({
    key: "recurring.stopsOpen",
    default: undefined,
});

/** The route the pointer is on in the list; the map draws it. */
export const recurringHoverState = atom<string | undefined>({
    key: "recurring.hover",
    default: undefined,
});

export const recurringSearchState = atom<string>({
    key: "recurring.search",
    default: "",
});

export const recurringShowPausedState = atom<"active" | "paused" | "ended">({
    key: "recurring.paused",
    default: "active",
});

/** How the list is ordered: by the date it shows (today first, routes without a date last) or by name. Kept per browser. */
export const recurringSortState = atomWithPersistence<"date" | "name">("recurring.sort", "date");

/** Where the list was scrolled to, so going back from a route lands where it was left. */
export const recurringListScrollState = atom<number>({
    key: "recurring.listScroll",
    default: 0,
});
